import { useEffect, useRef, useState } from "react";
import { ready } from "@/lib/clerk";

/**
 * Clerk's Email Code sign-in, mounted into our own shell.
 *
 * `reason` explains why the user landed here when they were previously signed
 * in — being bounced with no explanation is the difference between "I've been
 * suspended" and "this app is broken".
 */
export function SignIn({ reason }: { reason?: { title: string; body: string } }) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let node: HTMLElement | null = null;
    let cancelled = false;

    void ready()
      .then((clerk) => {
        if (cancelled || !host.current) return;
        node = host.current;
        clerk.mountSignIn(node, {
          /**
           * ⚠ `virtual` routing, not the default `path`.
           *
           * With path routing the component expects to own a dedicated route
           * and will navigate to the instance's configured sign-in URL — which
           * on this Clerk instance is the Account Portal at
           * `accounts.leychoon.app`. That redirect, followed by the portal
           * sending the browser back, is a loop the app can never settle.
           * Virtual routing keeps the whole flow inside this mounted node.
           */
          routing: "virtual",
          /**
           * ⚠ Absolute, and forced.
           *
           * The instance's `after_sign_in_url` points at `sso.leychoon.app`, so
           * without this a successful sign-in here would hand the user to a
           * different app entirely. Forcing it keeps them on the directory they
           * were trying to reach.
           */
          forceRedirectUrl: `${window.location.origin}/`,
          fallbackRedirectUrl: `${window.location.origin}/`,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setError(
            "Sign-in could not load. Check your connection, and turn off any ad or tracker blocker (including the browser's built-in one) for leychoon.app, then reload the page.",
          );
        }
      });

    return () => {
      cancelled = true;
      if (node) window.Clerk?.unmountSignIn(node);
    };
  }, []);

  return (
    <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-6 py-10">
      <div className="w-full">
        <div className="mb-6 text-center">
          <img
            src="/logo.svg"
            alt=""
            width={48}
            height={48}
            className="mx-auto mb-4 rounded-xl"
          />
          <h1 className="text-lg font-semibold">
            {reason?.title ?? "Ley Choon Apps"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {reason?.body ?? "Sign in with your Ley Choon email to see your apps."}
          </p>
        </div>

        {error ? (
          <p className="text-destructive text-center text-sm">{error}</p>
        ) : (
          <div ref={host} className="flex justify-center" />
        )}
      </div>
    </main>
  );
}
